import React, { useState } from "react";

import { Button, message, Modal, Steps, Typography, Upload, Alert } from "antd";
import styled from "styled-components";

import {
  CheckCircleOutlined,
  DownloadOutlined,
  UploadOutlined,
} from "@ant-design/icons";

import env from "@beam-australia/react-env";
import { useAuth } from "oidc-react";

const REACT_APP_API_URL = env("API_URL");

const { Step } = Steps;
const { Paragraph } = Typography;

const SDownloadTemplateWrap = styled.div`
  margin-bottom: 20px;
`;

const ImportDataModal = ({ isModalVisible, setIsModalVisible }: any) => {
  const [loading, setLoading] = useState(false);
  const auth = useAuth();

  const [dwnBtnActive, setDwnBtnActive] = useState(false);
  const [uplBtnActive, setUplBtnActive] = useState(false);
  const [stepsProgress, setStepsProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const handleCancel = () => {
    setIsModalVisible(false);
    setError(null); // Reset error state when closing the modal
  };

  const dwnBtnClick = () => {
    setDwnBtnActive(true);
    if (stepsProgress === 0) setStepsProgress(1);
  };

  const uplBtnClick = () => {
    setDwnBtnActive(true);
    setUplBtnActive(true);
    setStepsProgress(2);
  };

  const uploadProps = {
    name: "file",
    action: `${REACT_APP_API_URL}/terms/import`,
    headers: {
      Authorization: `Bearer ${auth.userData.access_token}`,
    },
    defaultFileList: [] as any[],
    multiple: false,
    beforeUpload: (file: any) => {
      setLoading(true);
      const isExcel =
        file.type ===
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      if (!isExcel) {
        message.error(`${file.name} is not a .xlsx file`);
        return Upload.LIST_IGNORE;
      }
      return true;
    },
    onChange(info: any) {
      setLoading(true);
      setError(null); // Clear previous errors

      if (info.file.status === "done") {
        const response = info.file.response;
        if (response && response.success) {
          message.success(
            `${info.file.name} import completed successfully. ${response.imported || 0} terms imported.`,
          );
          setLoading(false);
          setIsModalVisible(false);
        } else if (response && !response.success) {
          // Handle success: false response
          setError(response.message || "Import failed with an unknown error.");
          setLoading(false);
        }
      } else if (info.file.status === "error") {
        let errorMessage = `${info.file.name} import failed.`;

        const errorResponse = info.file.response;
        if (errorResponse && errorResponse.message) {
          errorMessage = errorResponse.message;
        }

        setError(errorMessage);
        setLoading(false);
      }
    },
  };

  return (
    <Modal
      title={`Import Data`}
      width={750}
      open={isModalVisible}
      onCancel={handleCancel}
      footer={[
        <Button key="back" onClick={handleCancel}>
          Cancel
        </Button>,
      ]}
    >
      <Paragraph>
        To import legacy transaction deals to the Commercial Databank, please
        follow the below process. Records imported this way will be tracked as
        'Historical Transactions' and will not require review.
      </Paragraph>
      <SDownloadTemplateWrap>
        <form method="get" action="template/CDB_Template.xlsx">
          <Button
            icon={dwnBtnActive ? <CheckCircleOutlined /> : <DownloadOutlined />}
            onClick={() => dwnBtnClick()}
            ghost={dwnBtnActive}
            type={dwnBtnActive ? "primary" : "default"}
            htmlType="submit"
          >
            Download Template
          </Button>
        </form>
      </SDownloadTemplateWrap>

      <Steps direction="vertical" size="small" current={stepsProgress}>
        <Step
          title="Template"
          description="Download the Excel template above."
        />
        <Step title="Populate" description="Fill in each row." />
        <Step
          title="Reupload"
          description="Upload the populated excel sheet below and submit."
        />
      </Steps>

      {error && (
        <div style={{ marginTop: 16, marginBottom: 16 }}>
          <Alert
            message="Import Error"
            description={error}
            type="error"
            showIcon
          />
        </div>
      )}

      <Upload {...uploadProps}>
        <Button
          icon={uplBtnActive ? <CheckCircleOutlined /> : <UploadOutlined />}
          onClick={() => uplBtnClick()}
          ghost={uplBtnActive}
          type={uplBtnActive ? "primary" : "default"}
          loading={loading}
        >
          Upload Template
        </Button>
      </Upload>
    </Modal>
  );
};

export default ImportDataModal;
